// #6275: the transaction column settings panel, shared by Ledgers and Events & Transactions. Same
// pattern as the cap table configuration: every column in use sits in one list on the right, with an
// export checkbox (Ledgers only -- Events & Transactions has no export) and an eye showing whether it's
// in the table, set from the Edit column window. Removing a column turns both off and sends it back to the left.

import {
	TransactionColumnRegistry,
	findColumnLabelConflict,
	getCustomColumnLabelProblem,
	previewCustomColumnKey,
	createPendingCustomColumn,
} from './TransactionColumnRegistry.js';
import { openCustomColumnModal, CUSTOM_COLUMN_FORMAT_LABELS } from './custom-column-modal.js';

const esc = (v) =>
	String(v ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/"/g, '&quot;');

/**
 * Renders the settings panels into `availableEl` / `selectedEl` and wires `addButtonEl`.
 * Safe to call again to reset to the saved state.
 *
 * @param {Object} opts
 * @param {Object} opts.clientData
 * @param {Array} opts.columnDefs - the page's getAllStandardColumns(clientData)
 * @param {HTMLElement} opts.availableEl - columns not in use
 * @param {HTMLElement} opts.selectedEl - the column list
 * @param {HTMLElement} [opts.addButtonEl] - "Create New" custom column button
 * @param {HTMLInputElement} [opts.searchEl] - filters the available columns by label
 * @param {boolean} opts.showExport - whether this page's table has an export
 * @returns {{ getState: () => {visibleKeys: Set, labelEdits: Object, formatEdits: Object, exportEdits: Object, pendingColumns: Array} }}
 */
export function initTransactionColumnSettings({ clientData, columnDefs, availableEl, selectedEl, addButtonEl, searchEl, showExport }) {
	const registry = new TransactionColumnRegistry(clientData);
	const visibleKeys = new Set(registry.getVisibleColumns().map((c) => c.key));
	columnDefs.forEach((col) => {
		if (col.locked) visibleKeys.add(col.key);
	});

	const labelEdits = {};
	columnDefs.forEach((col) => {
		// defaultLabel covers a column the registry doesn't know yet
		labelEdits[col.key] = registry.getLabel(col.key) || col.defaultLabel;
	});

	// Custom columns only -- built-ins have a fixed format elsewhere.
	const formatEdits = {};
	const exportEdits = {};
	columnDefs.forEach((col) => {
		if (!col._isCustomSlot) return;
		formatEdits[col.key] = col.format || 'text';
		exportEdits[col.key] = col.includeInExport !== false;
	});
	if (showExport) {
		// built-ins: exported now when visible or opted in (see registry.getExportColumns)
		const exportedNow = new Set(registry.getExportColumns().map((c) => c.key));
		columnDefs.forEach((col) => {
			if (!col._isCustomSlot && registry.canExport(col.key)) exportEdits[col.key] = exportedNow.has(col.key);
		});
	}

	// columns added in the modal, created on Save
	const pendingColumns = [];
	let pendingSeq = 0;

	let draggedKey = null;

	const hasExport = (col) => showExport && col.key in exportEdits;
	const inList = (col) => col.locked || visibleKeys.has(col.key) || (hasExport(col) && exportEdits[col.key]);

	function addColumn(key) {
		const col = columnDefs.find((c) => c.key === key);
		if (!col) return;
		visibleKeys.add(key);
		if (hasExport(col)) exportEdits[key] = true;
		renderPanels();
	}

	function removeColumn(col) {
		visibleKeys.delete(col.key);
		if (hasExport(col)) exportEdits[col.key] = false;
		renderPanels();
	}

	function labelProblem(label, excludeKey, checkName) {
		const conflictKey = findColumnLabelConflict(label, excludeKey, labelEdits);
		if (conflictKey) return `Already used by "${columnDefs.find((c) => c.key === conflictKey)?.defaultLabel || conflictKey}"`;
		return checkName ? getCustomColumnLabelProblem(label) : null;
	}

	function openAddModal() {
		openCustomColumnModal({
			title: 'Add custom column',
			applyText: 'Add',
			showExport,
			showVisibility: showExport,
			identity: (label) => ({ type: 'custom', key: previewCustomColumnKey(label, clientData, pendingColumns, labelEdits) }),
			validate: (label) => labelProblem(label, null, true),
			onApply: (values) => {
				const col = createPendingCustomColumn(++pendingSeq, values);
				columnDefs.push(col);
				pendingColumns.push(col);
				labelEdits[col.key] = values.label;
				formatEdits[col.key] = values.format;
				exportEdits[col.key] = values.includeInExport;
				if (values.showInTableUI) visibleKeys.add(col.key);
				renderPanels();
			},
		});
	}

	function openEditModal(col) {
		const builtIn = !col._isCustomSlot;
		openCustomColumnModal({
			title: 'Edit column',
			applyText: 'Apply',
			showExport: hasExport(col),
			lockFormat: builtIn,
			identity: col._isNewCustom
				? (label) => ({ type: 'custom', key: previewCustomColumnKey(label, clientData, pendingColumns, labelEdits, col.key) })
				: { type: builtIn ? 'standard' : 'custom', key: col.key },
			defaultLabel: builtIn ? col.defaultLabel : '',
			lockExport: !!col.locked,
			showVisibility: showExport && !col.locked,
			initial: {
				label: labelEdits[col.key],
				format: builtIn ? registry.getColumn(col.key)?.format || 'text' : formatEdits[col.key],
				includeInExport: exportEdits[col.key],
				showInTableUI: visibleKeys.has(col.key),
			},
			validate: (label) => labelProblem(label, col.key, !builtIn && label !== labelEdits[col.key]),
			onApply: (values) => {
				labelEdits[col.key] = values.label;
				if (col._isNewCustom) col.defaultLabel = values.label;
				if (!builtIn) formatEdits[col.key] = values.format;
				if (hasExport(col) && !col.locked) exportEdits[col.key] = values.includeInExport;
				if (showExport && !col.locked) {
					if (values.showInTableUI) visibleKeys.add(col.key);
					else visibleKeys.delete(col.key);
				}
				renderPanels();
			},
		});
	}

	function renderAvailableCard(col) {
		const card = document.createElement('div');
		card.className = 'ctcc-field-card';
		card.dataset.colKey = col.key;
		card.draggable = true;
		card.innerHTML = `
			<i class="material-icons" style="font-size:1rem;color:#666;">add</i>
			<span class="field-name">${esc(labelEdits[col.key] || col.defaultLabel)}</span>
			${col._isCustomSlot ? '<i class="material-icons custom-col-edit" title="Edit column" style="font-size:1rem;color:#666;margin-left:auto;">edit</i>' : ''}`;
		card.querySelector('.custom-col-edit')?.addEventListener('click', function (e) {
			e.stopPropagation();
			openEditModal(col);
		});
		card.addEventListener('click', () => addColumn(col.key));
		card.addEventListener('dragstart', function (e) {
			draggedKey = col.key;
			e.dataTransfer.setData('text/plain', col.key);
			e.dataTransfer.effectAllowed = 'copy';
			card.classList.add('dragging');
		});
		card.addEventListener('dragend', function () {
			card.classList.remove('dragging');
			draggedKey = null;
		});
		return card;
	}

	function renderListItem(col) {
		const currentLabel = labelEdits[col.key];
		const isHiddenFromTable = !col.locked && !visibleKeys.has(col.key);
		const subtitle = col._isCustomSlot
			? CUSTOM_COLUMN_FORMAT_LABELS[formatEdits[col.key]] || 'Text'
			: currentLabel !== col.defaultLabel
				? col.defaultLabel + ' — renamed'
				: '';

		const item = document.createElement('div');
		item.dataset.colKey = col.key;
		item.className = col.locked
			? 'ctcc-selected-item ctcc-locked-item'
			: isHiddenFromTable
				? 'ctcc-selected-item ctcc-hidden-from-table'
				: 'ctcc-selected-item';
		item.style.alignItems = 'center';
		item.innerHTML = `
			${col.locked ? '<i class="material-icons drag-handle">lock</i>' : ''}
			<div class="txn-col-view" style="flex:1;min-width:0;">
				<span class="column-name">${esc(currentLabel)}</span>
				${subtitle ? `<span class="txn-col-base">${esc(subtitle)}</span>` : ''}
			</div>
			${
				hasExport(col)
					? `<label class="ctcc-include-export" title="${col.locked ? 'Always included in export' : 'Include in export'}">
				<input type="checkbox" class="ctcc-include-export-cb" ${exportEdits[col.key] ? 'checked' : ''} ${col.locked ? 'disabled' : ''}>
			</label>`
					: ''
			}
			<div class="column-actions">
				<i class="material-icons visibility-status" title="${isHiddenFromTable ? 'Hidden from the table' : 'Shown in the table'}">${isHiddenFromTable ? 'visibility_off' : 'visibility'}</i>
				<i class="material-icons edit-btn" title="Edit column">edit</i>
				${col.locked ? '' : '<i class="material-icons remove-btn" title="Remove">close</i>'}
			</div>`;

		const exportCb = item.querySelector('.ctcc-include-export-cb');
		exportCb?.addEventListener('change', function (e) {
			e.stopPropagation();
			exportEdits[col.key] = exportCb.checked;
			// a column that's neither shown nor exported leaves the list
			if (!inList(col)) renderPanels();
		});
		item.querySelector('.ctcc-include-export')?.addEventListener('click', (e) => e.stopPropagation());
		item.querySelector('.edit-btn').addEventListener('click', function (e) {
			e.stopPropagation();
			openEditModal(col);
		});
		item.querySelector('.remove-btn')?.addEventListener('click', function (e) {
			e.stopPropagation();
			removeColumn(col);
		});
		return item;
	}

	function renderPanels() {
		const searchTerm = (searchEl?.value || '').trim().toLowerCase();
		const matchesSearch = (col) =>
			!searchTerm ||
			String(labelEdits[col.key] || col.defaultLabel || '')
				.toLowerCase()
				.includes(searchTerm);
		availableEl.innerHTML = '';
		selectedEl.innerHTML = '';
		columnDefs.forEach((col) => {
			if (inList(col)) selectedEl.appendChild(renderListItem(col));
			else if (matchesSearch(col)) availableEl.appendChild(renderAvailableCard(col));
		});
		if (!availableEl.children.length) {
			availableEl.innerHTML = `<p style="color:#999;font-size:0.8rem;padding:0.5rem;">${searchTerm ? 'No matching columns.' : 'All columns are in use.'}</p>`;
		}
	}

	if (searchEl) {
		searchEl.value = '';
		searchEl.oninput = () => renderPanels();
	}
	if (addButtonEl) {
		addButtonEl.onclick = function (e) {
			e.preventDefault();
			openAddModal();
		};
	}
	// assigned, not added, so re-initializing doesn't stack handlers
	selectedEl.ondragover = function (e) {
		e.preventDefault();
		if (draggedKey) e.dataTransfer.dropEffect = 'copy';
	};
	selectedEl.ondrop = function (e) {
		e.preventDefault();
		if (!draggedKey) return;
		const key = draggedKey;
		draggedKey = null;
		addColumn(key);
	};

	renderPanels();

	return {
		getState: () => ({ visibleKeys, labelEdits, formatEdits, exportEdits, pendingColumns }),
	};
}
